import type { PipelineKind } from '../ludus-lead/types.ts'
import type { AuditStage } from './toml.ts'

/**
 * The stage graph and the node contract (architecture §8.1, §8.2, A-23) —
 * what the executor runs, as data. Pure by construction: this module imports
 * no runtime, reads no file and makes no call, and a test greps it to keep
 * it that way, because the same graph must answer identically in the engine
 * that enqueues, the worker that runs and the console that draws.
 *
 * Stage 0 (`snapshot`) and stages 8–9 (`marts`, `export`) are **audit-level**:
 * their state lives on `audits.state` and `jobs`, never on a card. Stages 1–7
 * are **card-level** and get a `card_processing` row per card, per version.
 */
export type CardStage =
  | 'transcribe'
  | 'pseudonymize'
  | 'classify_call'
  | 'extract_facts'
  | 'link'
  | 'qa_checklist'
  | 'segment'

export type AuditLevelStage = 'snapshot' | 'marts' | 'export'

export interface StageSpec {
  stage: AuditStage
  level: 'audit' | 'card'
  dependsOn: AuditStage[]
}

/**
 * Default order with the explicit edges. The A-23 parallelism is in the
 * edges, not the order: `qa_checklist` depends on `classify_call` and not on
 * `extract_facts`, and `link` may start straight after `snapshot`.
 */
export const STAGE_GRAPH: readonly StageSpec[] = [
  { stage: 'snapshot', level: 'audit', dependsOn: [] },
  { stage: 'transcribe', level: 'card', dependsOn: ['snapshot'] },
  { stage: 'pseudonymize', level: 'card', dependsOn: ['transcribe'] },
  { stage: 'classify_call', level: 'card', dependsOn: ['pseudonymize'] },
  { stage: 'extract_facts', level: 'card', dependsOn: ['classify_call'] },
  { stage: 'link', level: 'card', dependsOn: ['snapshot'] },
  { stage: 'qa_checklist', level: 'card', dependsOn: ['classify_call'] },
  { stage: 'segment', level: 'card', dependsOn: ['extract_facts', 'qa_checklist', 'link'] },
  { stage: 'marts', level: 'audit', dependsOn: ['segment'] },
  { stage: 'export', level: 'audit', dependsOn: ['marts'] },
] as const

/** The nine stages after the snapshot, in default order. */
export const RUNNABLE_STAGES: readonly AuditStage[] = STAGE_GRAPH.filter(
  (spec) => spec.stage !== 'snapshot',
).map((spec) => spec.stage)

export const CARD_STAGES: readonly CardStage[] = STAGE_GRAPH.filter(
  (spec) => spec.level === 'card',
).map((spec) => spec.stage as CardStage)

export function isCardStage(stage: string): stage is CardStage {
  return (CARD_STAGES as readonly string[]).includes(stage)
}

export function stageSpec(stage: AuditStage): StageSpec {
  const spec = STAGE_GRAPH.find((item) => item.stage === stage)
  if (spec === undefined) throw new RangeError(`unknown stage ${stage}`)
  return spec
}

export type StageGraphIssue =
  | { kind: 'missing_dependency'; stage: string; dependsOn: string }
  | { kind: 'cycle'; path: string[] }
  | { kind: 'duplicate'; stage: string }

/** A graph is valid when every edge names a stage it holds and no path returns to its start. */
export function validateStageGraph(graph: readonly StageSpec[]): StageGraphIssue[] {
  const issues: StageGraphIssue[] = []
  const known = new Map<string, StageSpec>()
  for (const spec of graph) {
    if (known.has(spec.stage)) issues.push({ kind: 'duplicate', stage: spec.stage })
    known.set(spec.stage, spec)
  }
  for (const spec of graph) {
    for (const dependency of spec.dependsOn) {
      if (!known.has(dependency)) {
        issues.push({ kind: 'missing_dependency', stage: spec.stage, dependsOn: dependency })
      }
    }
  }
  const state = new Map<string, 'visiting' | 'done'>()
  const visit = (stage: string, path: string[]): void => {
    const mark = state.get(stage)
    if (mark === 'done') return
    if (mark === 'visiting') {
      issues.push({ kind: 'cycle', path: [...path.slice(path.indexOf(stage)), stage] })
      return
    }
    state.set(stage, 'visiting')
    for (const dependency of known.get(stage)?.dependsOn ?? []) {
      if (known.has(dependency)) visit(dependency, [...path, stage])
    }
    state.set(stage, 'done')
  }
  for (const spec of graph) visit(spec.stage, [])
  return issues
}

/**
 * Which card stages a pipeline runs when it has declared none itself
 * (§5.3): a pipeline of calls runs every card stage; a pipeline of deals has
 * no audio and no call type, so it links, extracts facts from its notes and
 * checks its stages against its history.
 */
export function defaultCardStagesFor(kind: PipelineKind): CardStage[] {
  return kind === 'interaction' ? [...CARD_STAGES] : ['link', 'extract_facts', 'qa_checklist']
}

/* ------------------------------------------------------------- versions */

/**
 * `version = hash(prompt | model | rules | adapter)` (§8.1): a stable hash of
 * what a stage's result depends on, so a change to any of the four makes new
 * rows beside the old and an unchanged one re-runs nothing. Inputs are
 * strings the caller has already canonicalised (a rules hash, a commit).
 */
export interface StageVersionInput {
  prompt: string
  model: string
  rules: string
  adapter: string
}

export function stageVersion(input: StageVersionInput): string {
  const material = [input.prompt, input.model, input.rules, input.adapter]
    .map((part) => `${part.length}:${part}`)
    .join('|')
  return sha256Hex(material)
}

/* --------------------------------------------------------- node contract */

/**
 * Why a unit produced nothing — closed (D-14): the absence of an input is a
 * fact about the card, never a failure. `failed` is for a fault with an
 * input present.
 */
export type SkipReason = 'no_audio' | 'no_transcript' | 'stage_disabled' | 'no_communications'

export const SKIP_REASONS: readonly SkipReason[] = [
  'no_audio',
  'no_transcript',
  'stage_disabled',
  'no_communications',
] as const

/** One line of `costs`, as a node reports it. */
export interface StageCost {
  provider: string
  unit: 'audio_sec' | 'tokens_in' | 'tokens_out' | 'calls'
  quantity: number
  amount: number
  currency: string
}

export type StageNodeResult =
  | { status: 'done'; output?: unknown; costs?: StageCost[] }
  | { status: 'skipped'; reason: SkipReason }
  | { status: 'failed'; error: string }

export interface StageNodeContext {
  auditId: string
  leadId: string
  stage: CardStage
  version: string
  pipelineId: string
  pipelineKind: PipelineKind
}

/**
 * A stage body: one card in, one result out. The executor owns the claim, the
 * lease, the effect key and the batch that writes the result (ADR-0007's
 * forms); the node owns only the work — a 30–60 second call is expected and
 * is the reason the executor is not the flow pump.
 */
export type StageNode = (context: StageNodeContext) => Promise<StageNodeResult>

/* ----------------------------------------------------------- sha-256 */

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
])

function rotr(x: number, n: number): number {
  return (x >>> n) | (x << (32 - n))
}

/** SHA-256 over UTF-8, written here so core stays free of `node:crypto` and `bun:` alike. */
export function sha256Hex(text: string): string {
  const bytes = new TextEncoder().encode(text)
  const bitLength = bytes.length * 8
  const padded = new Uint8Array(Math.ceil((bytes.length + 9) / 64) * 64)
  padded.set(bytes)
  padded[bytes.length] = 0x80
  const view = new DataView(padded.buffer)
  view.setUint32(padded.length - 8, Math.floor(bitLength / 0x100000000), false)
  view.setUint32(padded.length - 4, bitLength >>> 0, false)

  const h = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ])
  const w = new Uint32Array(64)
  for (let offset = 0; offset < padded.length; offset += 64) {
    for (let i = 0; i < 16; i += 1) w[i] = view.getUint32(offset + i * 4, false)
    for (let i = 16; i < 64; i += 1) {
      const s0 = rotr(w[i - 15]!, 7) ^ rotr(w[i - 15]!, 18) ^ (w[i - 15]! >>> 3)
      const s1 = rotr(w[i - 2]!, 17) ^ rotr(w[i - 2]!, 19) ^ (w[i - 2]! >>> 10)
      w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) >>> 0
    }
    let [a, b, c, d, e, f, g, hh] = [h[0]!, h[1]!, h[2]!, h[3]!, h[4]!, h[5]!, h[6]!, h[7]!]
    for (let i = 0; i < 64; i += 1) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)
      const ch = (e & f) ^ (~e & g)
      const t1 = (hh + S1 + ch + K[i]! + w[i]!) >>> 0
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)
      const maj = (a & b) ^ (a & c) ^ (b & c)
      const t2 = (S0 + maj) >>> 0
      hh = g
      g = f
      f = e
      e = (d + t1) >>> 0
      d = c
      c = b
      b = a
      a = (t1 + t2) >>> 0
    }
    h[0] = (h[0]! + a) >>> 0
    h[1] = (h[1]! + b) >>> 0
    h[2] = (h[2]! + c) >>> 0
    h[3] = (h[3]! + d) >>> 0
    h[4] = (h[4]! + e) >>> 0
    h[5] = (h[5]! + f) >>> 0
    h[6] = (h[6]! + g) >>> 0
    h[7] = (h[7]! + hh) >>> 0
  }
  return Array.from(h, (word) => word.toString(16).padStart(8, '0')).join('')
}
